import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { useFormsMaster } from "../../contexts/FormsMasterContext";
import Modal from "../../components/UI/Modal";
import PurposeForm from "../../components/Form/PurposeForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import Skeleton from "react-loading-skeleton";
import { X, User, Mail, Shield, Clock, Calendar, UserCheck, Building, MoreVertical, Search } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";

const FormsMaster = React.memo(() => {
  const { purposes, addPurpose, updatePurpose, deletePurpose, isLoading } = useFormsMaster();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPurpose, setEditingPurpose] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");


  // Separate component for actions column to avoid hooks rule violation
  const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteApproverModule }) => {
    const buttonRef = useRef(null);
    
    return (
      <div className="flex items-center justify-center relative">
        <button
          ref={buttonRef}
          onClick={() =>
            setOpenMenu(openMenu === row.id ? null : row.id)
          }
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </button>

        {openMenu === row.id && (
          <DropdownMenu
            buttonRef={buttonRef}
            onClose={() => setOpenMenu(null)}
          >
            <button
              onClick={(e) => {
                openModalForEdit(row);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              Edit
            </button>
            <DeleteButton
              style={`text-red-600 hover:text-red-800 px-3 py-2`}
              onDelete={(e) => {
                handleDeleteApproverModule(row.id);
                setOpenMenu(null);
              }}
            />
          </DropdownMenu>
        )}
      </div>
    );
  });

  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingPurpose(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((form_purpose) => {
    setEditingPurpose(form_purpose);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingPurpose(null);
    setFormErrors({});
    setMessage("");
  }, []);


  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  const handleFormSubmit = useCallback(
    async (purpose) => {
      setLoading(true);
      try {
        if (editingPurpose) {
          // console.log("purpose payload", purpose);
        
          // Convert FormData to object
          // console.log(purpose instanceof FormData)
          const formPurpose = purpose instanceof FormData ? convertFormDataToObject(purpose) : purpose;
  
          // console.log("Payload being sent:", { ...editingPurpose, ...formPurpose });
  
          await updatePurpose({ ...editingPurpose, ...formPurpose });
          setMessage("Purpose updated successfully.");
          setNotification({
            message: "Purpose updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create purpose", purpose)
          const formPurpose = purpose instanceof FormData
              ? convertFormDataToObject(purpose)
              : purpose;
          // console.log("formPurpose", formPurpose)
          await addPurpose(formPurpose);
          setMessage("Purpose added successfully.");
          setNotification({
            message: "Purpose added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
        } else {
          setNotification({
            message: `Unexpected error during submission:, ${err}`,
            type: "error",
          });
        }
      } finally {
        setLoading(false); 
      }
    },
    [editingPurpose, addPurpose, updatePurpose, closeModal]
  );

  // Memoize form_purposes
  const purposeForTable = useMemo(
    () =>
      purposes.map((purpose) => ({
        ...purpose,
        name: purpose?.name || "Unknown Purpose",
      })),
    [purposes]
  );

  const filteredPurposes = useMemo(() => {
    return purposeForTable.filter((item) => {
      const searchTerm = searchQuery.toLowerCase();
      return item.name.toLowerCase().includes(searchTerm);
    });
  }, [purposeForTable, searchQuery]);

  const handleDeletePurpose = async (id) => {
    try {
      await deletePurpose(id);
      setNotification({
        message: "Purpose deleted successfully",
        type: "success",
      });
    } catch (error) {
      setNotification({
        message: "Failed to delete purpose",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Name", field: "name" },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteApproverModule={handleDeletePurpose}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeletePurpose,
    ]
  );

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">Forms Masters</h2>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        {/* Left End: Search Bar */}
        <div className="relative w-full sm:max-w-xl"> 
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-poppins"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right End: Add Button */}
        <button
          onClick={openModalForAdd}
          className="bg-blue-600 font-medium text-white px-4 py-2 rounded-md hover:bg-blue-700 whitespace-nowrap"
        >
          Add Purpose
        </button>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      {/* Skeleton Loader */}
      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} height={40} />
          ))}
        </div>
      ) : (
        <div>
          <ReusableTable
            data={filteredPurposes}
            loading={isLoading}
            columns={columns}
            filterable={false}
            excludeFilterColumns={["actions"]}
          />

        </div>
      )}

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingPurpose ? "Edit Form Purpose" : "Add Form Purpose"}
      >
        <PurposeForm
          loading={loading} 
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingPurpose || { name: "", description: "", is_active: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default FormsMaster;
